import { tokenStore } from "@app/stores/auth.store.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useStore } from "@tanstack/react-store";
import { jwtDecode } from "jwt-decode";
import { useMemo } from "react";

interface JwtPayload {
    aud: string;
    exp: number;
    iss: string;
    userProfile: string;
}

interface UserData {
    UserId: number;
    UserName: string;
    Email: string;
    RoleId: string;
    RoleName: string;
    IsAdmin: boolean;
    IsManager: boolean;
    IsOutsideConsultant: boolean;
    IsInsideConsultant: boolean;
}

export default function useAuth() {
    const authToken = useStore(tokenStore);
    const queryClient = useQueryClient();

    const logout = async () => {
        tokenStore.setState(() => undefined);
        setTimeout(() => queryClient.clear(), 500);
    };

    const parsedToken = useMemo(() => {
        if (!authToken) {
            return false;
        }
        try {
            return jwtDecode<JwtPayload>(authToken);
        } catch {
            return undefined;
        }
    }, [authToken]);

    const isAuthenticated = useMemo(() => {
        if (!parsedToken) {
            return false;
        }
        return new Date(parsedToken.exp) <= new Date();
    }, [parsedToken]);

    const userData = useMemo<UserData | undefined>(() => {
        if (!parsedToken) {
            return undefined;
        }
        if (!parsedToken.userProfile) {
            return undefined;
        }
        return JSON.parse(parsedToken.userProfile);
    }, [parsedToken]);

    const isAdmin = useMemo(() => {
        return userData?.IsAdmin ?? false;
    }, [userData]);

    const isManager = useMemo(() => {
        return userData?.IsManager ?? false;
    }, [userData]);

    return {
        logout,
        isAuthenticated,
        userData,
        isAdmin,
        isManager,
    };
}
